import { useEffect, useState, useRef, useCallback } from "react";
import "./App.css";
import { PillarMark, WorkflowDiagram, ArchitectureDiagram, BlueprintCorner } from "./components/Diagrams";
import BootSequence from "./components/BootSequence";
import { useScrollProgress, useRAF } from "./hooks/useMotion";
import { useStripeCheckout } from "./hooks/useStripeCheckout";
import { useLenis } from "./design-tools/useLenis";
import { useGSAPReveal, useStaggerReveal } from "./design-tools/useGSAPReveal";
import ParticleField from "./design-tools/ParticleField";
import GlowLine from "./design-tools/GlowLine";
import VignetteOverlay from "./design-tools/VignetteOverlay";
import NoiseTexture from "./design-tools/NoiseTexture";
import { PALETTE, GLOW, SHADOW, MOTION } from "./design-tools/tokens";
import heroVideo from "./assets/dgb-hero.mp4";
import starsImg from "./assets/stars.png";

/* ─────────────────────────────────────────────
   SCROLL PROGRESS BAR
───────────────────────────────────────────── */
function ScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? (window.scrollY / max) * 100 : 0);
    };
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div
      className="scroll-progress"
      style={{ width: `${progress}%` }}
      aria-hidden="true"
    />
  );
}

/* ─────────────────────────────────────────────
   NAV
───────────────────────────────────────────── */
function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = ["About", "Services", "AI", "Web Dev", "APS", "Contact"];

  return (
    <>
      <nav
        data-testid="nav"
        style={{
          position: "fixed",
          top: 0, left: 0, right: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: scrolled ? "14px 56px" : "22px 56px",
          background: scrolled ? "rgba(10,18,16,0.92)" : "rgba(10,18,16,0.55)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: scrolled ? `1px solid ${PALETTE.border}` : "1px solid transparent",
          transition: `all ${MOTION.medium} ${MOTION.ease}`,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 500, fontSize: "15px", letterSpacing: "0.06em", color: PALETTE.white, lineHeight: 1 }}>
            Digital Billions Group
          </span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "8px", letterSpacing: "0.35em", textTransform: "uppercase", color: PALETTE.teal500 }}>
            Global Digital Innovation
          </span>
        </div>

        <div className="desktop-nav" style={{ display: "flex", gap: 36, alignItems: "center" }}>
          {links.map((item) => (
            <a key={item} href={`#${item.toLowerCase().replace(" ", "-")}`} className="nav-link">
              {item}
            </a>
          ))}
        </div>

        <button className={`hamburger${open ? " open" : ""}`} onClick={() => setOpen(!open)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </nav>

      <div className={`mobile-menu${open ? " open" : ""}`} role="dialog">
        {links.map((item) => (
          <a key={item} href={`#${item.toLowerCase().replace(" ", "-")}`} className="nav-link" onClick={() => setOpen(false)}>
            {item}
          </a>
        ))}
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", color: PALETTE.whiteFaint, marginTop: 20 }}>
          ◈ Digital Billions Group
        </span>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   HERO — video background, telemetry corners
───────────────────────────────────────────── */
function Hero() {
  const ref   = useRef();
  const time  = useRAF();
  const scrollP = useScrollProgress(ref);

  return (
    <section
      ref={ref}
      id="hero"
      data-testid="hero"
      className="hero-section"
      style={{
        minHeight: "100svh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        padding: "120px var(--pad-x) 100px",
        background: PALETTE.ink,
      }}
    >
      {/* ── Background video ── */}
      <video
        autoPlay muted loop playsInline
        className="hero-video"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          opacity: 0.28,
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <source src={heroVideo} type="video/mp4" />
      </video>

      {/* ── Gradient overlay to blend video with bg ── */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none",
        background: `
          radial-gradient(ellipse at 70% 30%, rgba(54,196,168,0.07), transparent 55%),
          linear-gradient(180deg, rgba(10,18,16,0.55) 0%, rgba(10,18,16,0.82) 80%, ${PALETTE.ink} 100%)
        `,
      }} aria-hidden="true" />

      {/* ── Bottom vignette — dissolves hero into next section ── */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 5, pointerEvents: "none",
        height: "220px",
        background: `linear-gradient(to bottom, transparent 0%, rgba(10,18,16,0.55) 40%, ${PALETTE.ink} 100%)`,
      }} aria-hidden="true" />

      <ParticleField />
      <VignetteOverlay strength={0.6} color={PALETTE.ink} />

      {/* Scan line */}
      <div className="scan-line" />

      {/* ── Blueprint corners — restored & enhanced ── */}
      {[
        { pos: "tl", top: 118, left: "var(--pad-x)" },
        { pos: "tr", top: 118, right: "var(--pad-x)" },
        { pos: "bl", bottom: 56, left: "var(--pad-x)" },
        { pos: "br", bottom: 56, right: "var(--pad-x)" },
      ].map(({ pos, top, bottom, left, right }) => (
        <div key={pos} style={{ position: "absolute", top, bottom, left, right, zIndex: 3 }}>
          <BlueprintCorner position={pos} ink={PALETTE.teal600} opacity={0.65} size={40} />
        </div>
      ))}

      {/* ── Telemetry readouts — top corners (hidden on mobile) ── */}
      <div className="hero-telemetry hero-telemetry-tl" style={{
        position: "absolute", top: 126, left: "calc(var(--pad-x) + 52px)",
        color: PALETTE.teal700, zIndex: 3,
        fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.28em",
        textTransform: "uppercase", lineHeight: 1.8,
      }}>
        <div>N 40°43′ &nbsp; W 74°00′</div>
        <div style={{ opacity: 0.65 }}>◌ &nbsp; ATELIER 01 &nbsp; ◌ &nbsp; DIGITAL BILLIONS</div>
      </div>
      <div className="hero-telemetry hero-telemetry-tr" style={{
        position: "absolute", top: 126, right: "calc(var(--pad-x) + 52px)",
        color: PALETTE.teal700, zIndex: 3, textAlign: "right",
        fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.28em",
        textTransform: "uppercase", lineHeight: 1.8,
      }}>
        <div>T+{time.toFixed(2)}s</div>
        <div style={{ opacity: 0.65 }}>◌ &nbsp; LIVE &nbsp; ◌ &nbsp; SIGNAL ACTIVE</div>
      </div>

      {/* ── Bottom telemetry (hidden on mobile) ── */}
      <div className="hero-telemetry hero-telemetry-bl" style={{
        position: "absolute", bottom: 66, left: "calc(var(--pad-x) + 52px)",
        color: PALETTE.teal700, zIndex: 3,
        fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.24em",
        textTransform: "uppercase",
      }}>
        SYS:READY &nbsp; ◌ &nbsp; MMXXVI
      </div>
      <div className="hero-telemetry hero-telemetry-br" style={{
        position: "absolute", bottom: 66, right: "calc(var(--pad-x) + 52px)",
        color: PALETTE.teal700, zIndex: 3, textAlign: "right",
        fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.24em",
        textTransform: "uppercase",
      }}>
        DBG-HQ &nbsp; ◌ &nbsp; GLOBAL
      </div>

      {/* ── Decorative vertical line ── */}
      <div style={{
        position: "absolute", top: 0, left: "var(--pad-x)",
        width: "1px", height: "100%",
        background: `linear-gradient(180deg, transparent 0%, ${PALETTE.teal800} 30%, transparent 100%)`,
        opacity: 0.5, zIndex: 2,
      }} aria-hidden="true" />

      {/* ── Schematic grid deco — right side ── */}
      <div style={{
        position: "absolute", top: "22%", right: "7%",
        width: "340px", height: "340px", zIndex: 2, opacity: 0.1, pointerEvents: "none",
      }} aria-hidden="true">
        <svg width="340" height="340" viewBox="0 0 340 340">
          {[0,1,2,3,4].map(i => (
            <line key={`h${i}`} x1="0" y1={i*85} x2="340" y2={i*85} stroke={PALETTE.teal500} strokeWidth="0.6" strokeDasharray="4 7" />
          ))}
          {[0,1,2,3,4].map(i => (
            <line key={`v${i}`} x1={i*85} y1="0" x2={i*85} y2="340" stroke={PALETTE.teal500} strokeWidth="0.6" strokeDasharray="4 7" />
          ))}
          <circle cx="170" cy="170" r="110" fill="none" stroke={PALETTE.teal500} strokeWidth="0.6" strokeDasharray="3 6" />
          <circle cx="170" cy="170" r="65"  fill="none" stroke={PALETTE.teal500} strokeWidth="0.5" />
          <circle cx="170" cy="170" r="4"   fill={PALETTE.teal500} />
        </svg>
      </div>

      {/* ── Copy ── */}
      <div className="hero-copy animate-hero-0" style={{
        position: "relative",
        zIndex: 4,
        transform: `translateY(${scrollP * -30}px)`,
        opacity: 1 - scrollP * 0.7,
      }}>
        <p className="teal-label animate-hero-0" style={{ marginBottom: 40 }}>
          ◈ &nbsp; DIGITAL BILLIONS GROUP &nbsp; ◈ &nbsp; GLOBAL DIGITAL INNOVATION
        </p>

        <h1 className="animate-hero-1" style={{
          fontFamily: "var(--font-display)", fontWeight: 300,
          fontSize: "clamp(52px, 9vw, 124px)", lineHeight: 0.9,
          letterSpacing: "-0.025em", color: PALETTE.white, marginBottom: 10,
        }}>
          Innovation,
        </h1>
        <h1 className="animate-hero-2" style={{
          fontFamily: "var(--font-display)", fontWeight: 300, fontStyle: "italic",
          fontSize: "clamp(52px, 9vw, 124px)", lineHeight: 0.9,
          letterSpacing: "-0.025em", color: PALETTE.teal400, marginBottom: 56,
          textShadow: GLOW.text,
        }}>
          Elegantly Deployed.
        </h1>

        <p className="animate-hero-3" style={{
          fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "17px",
          lineHeight: 1.85, color: PALETTE.whiteMid, maxWidth: "560px", marginBottom: 56,
          letterSpacing: "0.01em",
        }}>
          Where intelligent design meets refined technology. We build tomorrow's
          digital infrastructure — merging AI precision, automated workflows, and
          expert engineering into one cohesive vision.
        </p>

        <div className="animate-hero-4" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <a href="#services" className="btn-teal">
            <span>Explore Services</span>
            <span aria-hidden="true">↓</span>
          </a>
          <a href="#contact" className="btn-outline">
            <span>Start a Conversation</span>
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <div className="hero-side-label animate-hero-0" aria-hidden="true" style={{ zIndex: 4 }}>
        <div style={{ writingMode: "vertical-rl", fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", color: PALETTE.teal800 }}>
          Scroll to explore
        </div>
        <div style={{ width: "1px", height: "60px", background: `linear-gradient(180deg, ${PALETTE.teal700}, transparent)`, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "40%", background: PALETTE.teal500, animation: "scrollDot 2.4s cubic-bezier(0.5,0,0.5,1) infinite" }} />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   ABOUT
───────────────────────────────────────────── */
function About() {
  const ref = useRef();
  const p   = useScrollProgress(ref);

  return (
    <section ref={ref} id="about" data-testid="about" className="section-pad" style={{
      background: `linear-gradient(180deg, ${PALETTE.ink} 0%, #0e1e18 100%)`,
      position: "relative", overflow: "hidden",
    }}>
      <VignetteOverlay strength={0.3} color={PALETTE.ink} />
      <div className="two-col" style={{ position: "relative", zIndex: 2 }}>
        {/* Left */}
        <div className="gsap-reveal">
          <p className="teal-label" style={{ marginBottom: 32 }}>§ &nbsp; About Us</p>
          <GlowLine style={{ marginBottom: 32, maxWidth: 40 }} />
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: "clamp(32px, 4vw, 52px)", lineHeight: 1.08, letterSpacing: "-0.01em", color: PALETTE.white }}>
            Crafting the Future<br />
            <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>in Digital Form.</em>
          </h2>
          <svg width="200" height="200" viewBox="0 0 220 220" style={{ marginTop: 48, opacity: 0.75 }}>
            <circle cx="110" cy="110" r="80" fill="none" stroke={PALETTE.teal600} strokeWidth="0.8" />
            <circle cx="110" cy="110" r="55" fill="none" stroke={PALETTE.teal500} strokeWidth="0.6" strokeDasharray="2 3" opacity="0.6" />
            <circle cx="110" cy="110" r="30" fill="none" stroke={PALETTE.teal500} strokeWidth="0.6" />
            <circle cx="110" cy="110" r="3" fill={PALETTE.teal400} />
            <g transform={`rotate(${p * 60} 110 110)`}>
              <circle cx="190" cy="110" r="2" fill={PALETTE.teal500} />
              <circle cx="30" cy="110" r="2" fill={PALETTE.teal500} />
            </g>
            <g transform={`rotate(${-p * 40 + 45} 110 110)`}>
              <circle cx="165" cy="110" r="2" fill={PALETTE.teal400} />
            </g>
            <text x="110" y="22" textAnchor="middle" style={{ fontFamily: "var(--font-mono)", fontSize: 8, letterSpacing: "0.2em", fill: PALETTE.teal600 }}>SYSTEMA</text>
          </svg>
        </div>

        {/* Right */}
        <div className="gsap-reveal" style={{ paddingTop: 4 }} data-reveal-delay="120">
          <p style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "17px", lineHeight: 1.85, color: PALETTE.whiteMid, marginBottom: 28 }}>
            Digital Billions Group is a global digital innovation firm serving forward-thinking
            brands, creators, and enterprises — from emerging startups to established
            organizations ready to evolve.
          </p>
          <p style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "17px", lineHeight: 1.85, color: PALETTE.whiteMid, marginBottom: 28 }}>
            We specialize in blending intelligent automation with purposeful engineering.
            Every service we offer connects people with technology — seamlessly,
            responsibly, and at scale.
          </p>
          <p style={{
            fontFamily: "var(--font-display)", fontStyle: "italic", fontWeight: 400,
            fontSize: "20px", lineHeight: 1.6, color: PALETTE.teal400,
            borderLeft: `2px solid ${PALETTE.teal700}`, paddingLeft: "20px", marginTop: 40,
            textShadow: GLOW.text,
          }}>
            Refine everything. Automate intelligently. Create with purpose.
          </p>

          {/* Stats */}
          <div className="stagger-container" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24, marginTop: 64, paddingTop: 32, borderTop: `1px solid ${PALETTE.border}` }}>
            {[
              { n: "05", l: "Pillars of practice" },
              { n: "∞",  l: "Iterations refined" },
              { n: "01", l: "Unifying philosophy" },
            ].map((s, i) => (
              <div key={i} className="stagger-child stat-card">
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 48, color: PALETTE.teal400, lineHeight: 1, textShadow: GLOW.text }}>
                  {s.n}
                </div>
                <div className="mono-tiny" style={{ marginTop: 8, color: PALETTE.teal600 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SERVICES / OFFERINGS
───────────────────────────────────────────── */
const offerings = [
  { number: "01", kind: "ai",        glyph: "◎", title: "Agentic AI Workflows",     description: "Custom AI agents that learn your business and work around the clock. We build systems that answer questions, qualify leads, schedule appointments, and handle routine tasks — automatically." },
  { number: "02", kind: "vending",   glyph: "◫", title: "Digital Vending",           description: "From luxury retail kiosks to themed entertainment machines, our vending systems turn passive transactions into memorable experiences. Aesthetics and strategy, elegantly combined." },
  { number: "03", kind: "media",     glyph: "◈", title: "Multi‑Media Publishing",    description: "We amplify artists, creators, and performance brands through our global publishing network. Creation and distribution, handled with professionalism and precision." },
  { number: "04", kind: "marketing", glyph: "◉", title: "Digital Marketing",         description: "White-glove marketing built for brands that demand more. Analytics meets artistry across Meta, TikTok, Google, and bespoke SEO campaigns designed to convert." },
  { number: "05", kind: "app",       glyph: "◉", title: "Application Development",   description: "Polished static sites to enterprise-grade cross-platform platforms. Every solution is architected for performance, built for scale, and designed to last." },
];

function OfferingRow({ o, i }) {
  const ref     = useRef();
  const p       = useScrollProgress(ref);
  const t       = useRAF();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={ref}
      className="offering-row"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        opacity: 0.25 + Math.min(1, p * 2.5) * 0.75,
        transform: `translateX(${(1 - Math.min(1, p * 2.5)) * -20}px)`,
        transition: "opacity 0.6s var(--ease), transform 0.6s var(--ease)",
      }}
    >
      <div className="offering-inner">
        <div className="offering-pillar" style={{ display: "flex", justifyContent: "center", transform: hovered ? "scale(1.08)" : "scale(1)", transition: `transform ${MOTION.medium} ${MOTION.ease}`, filter: hovered ? `drop-shadow(0 0 10px ${PALETTE.teal600})` : "none" }}>
          <PillarMark kind={o.kind} size={100} t={t} ink={PALETTE.diagramInk} accent={PALETTE.teal400} />
        </div>
        <div className="offering-num" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", color: PALETTE.teal700, textTransform: "uppercase" }}>{o.number}</span>
          <span style={{ fontSize: "18px", color: PALETTE.teal500, lineHeight: 1 }}>{o.glyph}</span>
        </div>
        <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 400, fontSize: "clamp(20px, 2.5vw, 30px)", lineHeight: 1.15, letterSpacing: "-0.01em", color: hovered ? PALETTE.teal300 : PALETTE.white, transition: `color ${MOTION.fast} ease`, paddingTop: 4 }}>
          {o.title}
        </h3>
        <p className="offering-desc" style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "15px", lineHeight: 1.8, color: PALETTE.whiteMid }}>
          {o.description}
        </p>
      </div>
    </div>
  );
}

function Services() {
  return (
    <section id="services" data-testid="services" className="section-pad" style={{ background: "#0d1a18", position: "relative" }}>
      <div className="offerings-header gsap-reveal">
        <div>
          <p className="teal-label" style={{ marginBottom: 20 }}>◈ &nbsp; What We Do</p>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: "clamp(32px, 4vw, 56px)", lineHeight: 1, letterSpacing: "-0.01em", color: PALETTE.white }}>
            Our Services
          </h2>
        </div>
        <p className="sub-label" style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: PALETTE.teal600, maxWidth: "220px", textAlign: "right", lineHeight: 1.7 }}>
          Five pillars of digital excellence — each distinct, all connected.
        </p>
      </div>
      <div>
        {offerings.map((o, i) => <OfferingRow key={o.number} o={o} i={i} />)}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   ATMOSPHERIC QUOTE
───────────────────────────────────────────── */
function AtmosphericDivider() {
  const ref = useRef();
  const p   = useScrollProgress(ref);
  return (
    <section ref={ref} className="atm-divider section-pad" style={{ position: "relative", overflow: "hidden" }}>
      <ParticleField style={{ opacity: 0.35 }} />
      <VignetteOverlay strength={0.5} color="#0f1f1a" />
      <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center", position: "relative", zIndex: 2, transform: `translateY(${(p - 0.5) * -50}px)` }}>
        <p className="teal-label" style={{ marginBottom: 32 }}>◈ &nbsp; A Quiet Philosophy</p>
        <GlowLine style={{ margin: "0 auto 40px", maxWidth: 120 }} animated />
        <p style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontStyle: "italic", fontSize: "clamp(26px, 3.5vw, 46px)", lineHeight: 1.35, letterSpacing: "-0.01em", color: PALETTE.white }}>
          "Technology should not just serve society—<br />
          <span style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>it should elevate it."</span>
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   AI SERVICES
───────────────────────────────────────────── */
const aiPlans = [
  {
    tier: "01", glyph: "◎", name: "AI Strategy Call", price: "$99", cadence: "One Time", tag: "Start here", featured: false,
    description: "30 minutes with our automation team. Perfect for business owners exploring AI before committing to a full system.",
    includes: ["Review of your current workflows", "Identify automation opportunities", "Recommended AI assistant approach", "Simple implementation roadmap"],
    cta: "Book My Strategy Call", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_AI_STRATEGY_CALL",
  },
  {
    tier: "02", glyph: "◉", name: "Starter Assistant", price: "$299", cadence: "/ month", tag: "Most popular", featured: true,
    description: "A 24/7 AI assistant handling inquiries, bookings, and lead capture. Your business, always on.",
    includes: ["AI website chat assistant", "Automated appointment booking", "Lead capture and notifications", "Basic workflow automation", "Monthly monitoring"],
    cta: "Start My AI Assistant", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_AI_STARTER",
  },
  {
    tier: "03", glyph: "◈", name: "Business Automation", price: "$799", cadence: "/ month", tag: "Full stack", featured: false,
    description: "End-to-end automation: customer comms, scheduling, and CRM — fully integrated.",
    includes: ["AI website & messaging assistant", "Automated booking system", "Email and SMS follow-ups", "CRM integration", "Lead qualification workflows", "Monthly optimization"],
    cta: "Launch My Automation", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_AI_BUSINESS",
  },
  {
    tier: "04", glyph: "◫", name: "Advanced Workflow Systems", price: "From $2,500", cadence: "/ month", tag: "Enterprise", featured: false,
    description: "Multi-department automation built around your business processes. Fully customized.",
    includes: ["AI customer service teams", "Sales and lead management", "Operations workflow automation", "Internal AI assistants for staff", "Data processing and reporting"],
    cta: "Schedule Consultation", ctaSymbol: "↗", stripeEnv: "REACT_APP_STRIPE_AI_ENTERPRISE",
  },
];

function PricingCard({ plan }) {
  const { checkout, loading } = useStripeCheckout();
  const [hovered, setHovered] = useState(false);
  const featured = plan.featured;
  // Strip REACT_APP_ prefix — backend accepts the bare key and handles one-time vs subscription
  const priceKey = plan.stripeEnv ? plan.stripeEnv.replace(/^REACT_APP_/, "") : null;

  const cardBg = featured
    ? "linear-gradient(160deg, #1a3530 0%, #0f2420 100%)"
    : "linear-gradient(160deg, #112418 0%, #0a1a16 100%)";

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${featured ? "rgba(102,213,197,0.35)" : "rgba(53,143,128,0.18)"}`,
        padding: "32px 24px 32px",
        display: "flex", flexDirection: "column",
        transition: `all ${MOTION.medium} ${MOTION.ease}`,
        boxShadow: hovered ? (featured ? "0 0 30px rgba(54,196,168,0.2), 0 0 60px rgba(54,196,168,0.08)" : GLOW.card) : "none",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        position: "relative", overflow: "hidden",
        minHeight: 380,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={featured ? "pricing-card-hatch-featured" : "pricing-card-hatch"} />
      {featured && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, transparent, rgba(54,196,168,0.6), transparent)` }} />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, position: "relative", zIndex: 1 }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", color: PALETTE.teal700 }}>{plan.tier}</span>
        <span style={{
          fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase",
          color: featured ? PALETTE.teal300 : PALETTE.teal500,
          border: `1px solid ${featured ? "rgba(102,213,197,0.4)" : "rgba(53,143,128,0.3)"}`,
          padding: "3px 9px",
          background: featured ? "rgba(54,196,168,0.08)" : "transparent",
        }}>
          {plan.tag}
        </span>
      </div>
      <div style={{ fontSize: "20px", color: featured ? PALETTE.teal400 : PALETTE.teal600, marginBottom: 14, position: "relative", zIndex: 1 }}>{plan.glyph}</div>
      <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 400, fontSize: "22px", lineHeight: 1.15, color: PALETTE.white, marginBottom: 12, position: "relative", zIndex: 1 }}>
        {plan.name}
      </h3>
      <div style={{ marginBottom: 20, position: "relative", zIndex: 1 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontStyle: "italic", fontSize: "32px", color: featured ? PALETTE.teal300 : PALETTE.white, letterSpacing: "-0.02em" }}>
          {plan.price}
        </span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.15em", color: PALETTE.teal700, marginLeft: 6 }}>
          {plan.cadence}
        </span>
      </div>
      <div style={{ height: "1px", background: `linear-gradient(90deg, ${featured ? "rgba(102,213,197,0.3)" : "rgba(53,143,128,0.18)"}, transparent)`, marginBottom: 20 }} />
      <p style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "13px", lineHeight: 1.75, color: PALETTE.whiteMid, marginBottom: 24, flexGrow: 1, position: "relative", zIndex: 1 }}>
        {plan.description}
      </p>
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 0", display: "flex", flexDirection: "column", gap: 7, position: "relative", zIndex: 1 }}>
        {plan.includes.map((item, i) => (
          <li key={i} style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "12px", lineHeight: 1.6, color: PALETTE.whiteMid, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ color: PALETTE.teal500, flexShrink: 0, marginTop: 2, fontSize: 10 }}>◌</span>
            {item}
          </li>
        ))}
      </ul>
      <button
        onClick={() => priceKey && checkout(priceKey)}
        disabled={loading || !priceKey}
        className={`pricing-cta-btn${featured ? " featured" : ""}`}
        style={{ marginTop: 28 }}
      >
        <span>{loading ? "Redirecting..." : plan.cta}</span>
        <span aria-hidden="true">{plan.ctaSymbol}</span>
      </button>
    </div>
  );
}

/* ─── CTA Band Button Components — use backend checkout flow ─── */
function AICTAButtons() {
  const { checkout, loading } = useStripeCheckout();
  return (
    <>
      <button onClick={() => checkout("STRIPE_AI_STARTER")} disabled={loading} className="btn-teal">
        <span>{loading ? "Redirecting..." : "Get Started With AI"}</span>
        <span aria-hidden="true">→</span>
      </button>
      <button onClick={() => checkout("STRIPE_AI_STRATEGY_CALL")} disabled={loading} className="btn-outline">
        <span>{loading ? "Redirecting..." : "Book $99 Strategy Call"}</span>
        <span aria-hidden="true">◎</span>
      </button>
    </>
  );
}

function WebDevCTAButtons() {
  const { checkout, loading } = useStripeCheckout();
  return (
    <>
      <button onClick={() => checkout("STRIPE_DEV_PKG_A")} disabled={loading} className="btn-teal">
        <span>{loading ? "Redirecting..." : "Start My Project"}</span>
        <span aria-hidden="true">→</span>
      </button>
      <button onClick={() => checkout("STRIPE_DEV_STRATEGY_CALL")} disabled={loading} className="btn-outline">
        <span>{loading ? "Redirecting..." : "Book $199 Consultation"}</span>
        <span aria-hidden="true">◎</span>
      </button>
    </>
  );
}

function AIServices() {
  const ref = useRef();
  const p   = useScrollProgress(ref);
  const t   = useRAF();

  return (
    <section
      ref={ref}
      id="ai"
      data-testid="ai-services"
      className="section-pad"
      style={{
        background: `linear-gradient(160deg, #162820 0%, #0a1210 100%)`,
        position: "relative",
        overflow: "hidden",
      }}>
      {/* Stars background — used once here */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          backgroundImage: `url(${starsImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.08,
          mixBlendMode: "screen",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "15%",
          right: "-8%",
          width: "500px",
          height: "500px",
          background: `radial-gradient(circle, rgba(54,196,168,0.05), transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <VignetteOverlay strength={0.4} color="#0a1210" />

      <div
        style={{
          position: "absolute",
          top: 32,
          left: 32,
          opacity: 0.4,
          zIndex: 2,
        }}>
        <BlueprintCorner position="tl" ink={PALETTE.teal600} />
      </div>
      <div
        style={{
          position: "absolute",
          top: 32,
          right: 32,
          opacity: 0.4,
          zIndex: 2,
        }}>
        <BlueprintCorner position="tr" ink={PALETTE.teal600} />
      </div>

      <div
        className="section-header-2col gsap-reveal"
        style={{ position: "relative", zIndex: 2 }}>
        <div>
          <p className="teal-label" style={{ marginBottom: 20 }}>
            ◉ &nbsp; AI & Automation
          </p>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(32px, 4vw, 56px)",
              lineHeight: 1,
              letterSpacing: "-0.01em",
              color: PALETTE.white,
            }}>
            AI Assistants &<br />
            <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>
              Automated Workflows
            </em>
          </h2>
        </div>
        <div>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "17px",
              lineHeight: 1.85,
              color: PALETTE.whiteMid,
              marginBottom: 24,
            }}>
            We build AI assistants and automated workflows that handle everyday
            business tasks. Your team focuses on growth. The system handles the
            rest.
          </p>
          <p
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "18px",
              color: PALETTE.teal400,
              borderLeft: `2px solid ${PALETTE.teal700}`,
              paddingLeft: "20px",
              textShadow: GLOW.text,
            }}>
            Think of it as a digital team member working for you 24/7.
          </p>
        </div>
      </div>

      <div
        className="diagram-frame gsap-reveal"
        style={{
          position: "relative",
          zIndex: 2,
          opacity: Math.min(1, p * 1.5),
          transform: `translateY(${(1 - Math.min(1, p * 1.5)) * 36}px)`,
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 24,
          }}>
          <span className="mono-tiny" style={{ color: PALETTE.teal500 }}>
            FIG. 01 · AGENTIC WORKFLOW SCHEMATIC
          </span>
          <span className="mono-tiny" style={{ color: PALETTE.teal600 }}>
            SCALE 1:1 &nbsp; ◌ &nbsp; LIVE
          </span>
        </div>
        <div style={{ height: 360 }}>
          <WorkflowDiagram
            width={1200}
            height={360}
            t={t}
            ink={PALETTE.diagramInk}
            accent={PALETTE.teal400}
          />
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 2, marginBottom: 80 }}>
        <div
          className="pricing-grid gsap-reveal"
          style={{ position: "relative", zIndex: 1 }}>
          {aiPlans.map((plan) => (
            <PricingCard key={plan.tier} plan={plan} />
          ))}
        </div>
      </div>

      <div
        className="cta-band gsap-reveal"
        style={{ position: "relative", zIndex: 2 }}>
        <div>
          <p className="teal-label" style={{ marginBottom: 16 }}>
            ◈ &nbsp; Ready to Add AI to Your Business?
          </p>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "15px",
              lineHeight: 1.75,
              color: PALETTE.whiteMid,
              maxWidth: "480px",
            }}>
            Start with a strategy session or launch your assistant today. Every
            engagement starts with a clear plan tailored to your workflows.
          </p>
        </div>
        <div className="cta-band-buttons">
          <AICTAButtons />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   WEB DEV SERVICES
───────────────────────────────────────────── */
const webDevPlans = [
  {
    tier: "01", glyph: "◎", name: "Discovery & Strategy", price: "$199", cadence: "One Time", tag: "Start here", featured: false,
    description: "A focused consultation to map your digital needs. We review goals, assess requirements, and deliver a clear development roadmap.",
    includes: ["Current digital presence review", "Technical requirements analysis", "Recommended tech stack", "Project scope and timeline"],
    cta: "Book My Consultation", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_DEV_STRATEGY_CALL",
  },
  {
    tier: "02", glyph: "◉", name: "Launchpad", price: "$400", cadence: "/ month", tag: "Most popular", featured: true,
    description: "A polished, fast, reliable online presence. We design, build, and maintain static websites that convert visitors into clients.",
    includes: ["Custom-designed static website", "Mobile-responsive layout", "SEO-ready structure", "Hosting setup & deployment", "Monthly updates & support"],
    cta: "Launch My Website", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_DEV_PKG_A",
  },
  {
    tier: "03", glyph: "◈", name: "Architect", price: "$1,200", cadence: "/ month", tag: "Full stack", featured: false,
    description: "Dynamic applications — dashboards, portals, e-commerce, SaaS tools. Built to scale with your growth.",
    includes: ["Full-stack web application", "Custom backend & database", "User authentication & roles", "Third-party API integrations", "Performance optimization", "Ongoing maintenance"],
    cta: "Build My Application", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_DEV_PKG_B",
  },
  {
    tier: "04", glyph: "◫", name: "Omni Platform", price: "From $3,500", cadence: "/ month", tag: "Enterprise", featured: false,
    description: "Web, iOS, and Android from a single codebase. One ecosystem, deployed across every device your users rely on.",
    includes: ["Cross-platform web + iOS + Android", "Unified backend & API layer", "App Store & Play Store submission", "Push notifications & offline support", "Dedicated project management"],
    cta: "Schedule Platform Call", ctaSymbol: "↗", stripeEnv: "REACT_APP_STRIPE_DEV_PKG_C",
  },
];

function WebDevCard({ plan }) {
  const { checkout, loading } = useStripeCheckout();
  const [hovered, setHovered] = useState(false);
  const featured = plan.featured;
  // Strip REACT_APP_ prefix — backend accepts the bare key and handles one-time vs subscription
  const priceKey = plan.stripeEnv ? plan.stripeEnv.replace(/^REACT_APP_/, "") : null;

  const cardBg = featured
    ? "linear-gradient(160deg, #1a3530 0%, #0f2420 100%)"
    : "linear-gradient(160deg, #112418 0%, #0a1a16 100%)";

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${featured ? "rgba(102,213,197,0.35)" : "rgba(53,143,128,0.18)"}`,
        padding: "32px 24px 32px",
        display: "flex", flexDirection: "column",
        transition: `all ${MOTION.medium} ${MOTION.ease}`,
        boxShadow: hovered ? (featured ? "0 0 30px rgba(54,196,168,0.2)" : GLOW.card) : "none",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        position: "relative", overflow: "hidden",
        minHeight: 380,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={featured ? "pricing-card-hatch-featured" : "pricing-card-hatch"} />
      {featured && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, transparent, rgba(54,196,168,0.6), transparent)` }} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, position: "relative", zIndex: 1 }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", color: PALETTE.teal700 }}>{plan.tier}</span>
        <span style={{
          fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase",
          color: featured ? PALETTE.teal300 : PALETTE.teal500,
          border: `1px solid ${featured ? "rgba(102,213,197,0.4)" : "rgba(53,143,128,0.3)"}`,
          padding: "3px 9px",
          background: featured ? "rgba(54,196,168,0.08)" : "transparent",
        }}>
          {plan.tag}
        </span>
      </div>

      <div style={{ fontSize: "20px", color: featured ? PALETTE.teal400 : PALETTE.teal600, marginBottom: 14, position: "relative", zIndex: 1 }}>{plan.glyph}</div>
      <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 400, fontSize: "22px", lineHeight: 1.15, color: PALETTE.white, marginBottom: 12, position: "relative", zIndex: 1 }}>{plan.name}</h3>
      <div style={{ marginBottom: 20, position: "relative", zIndex: 1 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontStyle: "italic", fontSize: "32px", color: featured ? PALETTE.teal300 : PALETTE.white, letterSpacing: "-0.02em" }}>{plan.price}</span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.15em", color: PALETTE.teal700, marginLeft: 6 }}>{plan.cadence}</span>
      </div>
      <div style={{ height: "1px", background: `linear-gradient(90deg, ${featured ? "rgba(102,213,197,0.3)" : "rgba(53,143,128,0.18)"}, transparent)`, marginBottom: 20 }} />
      <p style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "13px", lineHeight: 1.75, color: PALETTE.whiteMid, marginBottom: 24, flexGrow: 1, position: "relative", zIndex: 1 }}>{plan.description}</p>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 7, position: "relative", zIndex: 1 }}>
        {plan.includes.map((item, i) => (
          <li key={i} style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "12px", lineHeight: 1.6, color: PALETTE.whiteMid, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ color: PALETTE.teal500, flexShrink: 0, marginTop: 2, fontSize: 10 }}>◌</span>
            {item}
          </li>
        ))}
      </ul>
      <button onClick={() => priceKey && checkout(priceKey)} disabled={loading || !priceKey} className={`pricing-cta-btn${featured ? " featured" : ""}`} style={{ marginTop: 28 }}>
        <span>{loading ? "Redirecting..." : plan.cta}</span>
        <span aria-hidden="true">{plan.ctaSymbol}</span>
      </button>
    </div>
  );
}

function WebDevServices() {
  const ref = useRef();
  const p   = useScrollProgress(ref);
  const t   = useRAF();

  return (
    <section
      ref={ref}
      id="web-dev"
      data-testid="webdev-services"
      className="section-pad"
      style={{
        background: "#0f1f1a",
        position: "relative",
        overflow: "hidden",
      }}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "25%",
          left: "-5%",
          width: "450px",
          height: "450px",
          background: `radial-gradient(circle, rgba(54,196,168,0.04), transparent 65%)`,
          pointerEvents: "none",
        }}
      />

      <div className="section-header-2col gsap-reveal">
        <div>
          <p className="teal-label" style={{ marginBottom: 20 }}>
            ◫ &nbsp; Web Development
          </p>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(32px, 4vw, 56px)",
              lineHeight: 1,
              letterSpacing: "-0.01em",
              color: PALETTE.white,
            }}>
            Platforms,
            <br />
            <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>
              Architected.
            </em>
          </h2>
        </div>
        <div>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "17px",
              lineHeight: 1.85,
              color: PALETTE.whiteMid,
              marginBottom: 24,
            }}>
            From a polished static presence to a full cross-platform product
            suite, we engineer digital experiences with clarity and precision.
            Built to perform. Designed to impress.
          </p>
          <p
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "18px",
              color: PALETTE.teal400,
              borderLeft: `2px solid ${PALETTE.teal700}`,
              paddingLeft: "20px",
              textShadow: GLOW.text,
            }}>
            Your vision, architected with purpose.
          </p>
        </div>
      </div>

      <div
        className="diagram-frame gsap-reveal"
        style={{
          opacity: Math.min(1, p * 1.5),
          transform: `translateY(${(1 - Math.min(1, p * 1.5)) * 36}px)`,
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 24,
          }}>
          <span className="mono-tiny" style={{ color: PALETTE.teal500 }}>
            FIG. 02 · PLATFORM ARCHITECTURE
          </span>
          <span className="mono-tiny" style={{ color: PALETTE.teal600 }}>
            SCALE 1:1 &nbsp; ◌ &nbsp; LIVE
          </span>
        </div>
        <div style={{ height: 360 }}>
          <ArchitectureDiagram
            width={1200}
            height={360}
            t={t}
            ink={PALETTE.diagramInk}
            accent={PALETTE.teal400}
          />
        </div>
      </div>

      <div style={{ position: "relative", marginBottom: 80 }}>
        <div
          className="pricing-grid gsap-reveal"
          style={{ position: "relative", zIndex: 1 }}>
          {webDevPlans.map((plan) => (
            <WebDevCard key={plan.tier} plan={plan} />
          ))}
        </div>
      </div>

      <div className="cta-band gsap-reveal">
        <div>
          <p className="teal-label" style={{ marginBottom: 16 }}>
            ◈ &nbsp; Ready to Build Something Great?
          </p>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "15px",
              lineHeight: 1.75,
              color: PALETTE.whiteMid,
              maxWidth: "480px",
            }}>
            From a stunning static site to a complex cross-platform application,
            we're ready to bring your project to life. Start with a consultation
            or jump straight in.
          </p>
        </div>
        <div className="cta-band-buttons">
          <WebDevCTAButtons />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   APS — ADVANCED PLATFORM SERVICES
───────────────────────────────────────────── */
const apsTiers = [
  {
    id: "discovery", badge: "Phase 01", glyph: "◎", tag: "Foundation",
    title: "Discovery & Architecture",
    description: "Operational mapping, compliance analysis, and infrastructure planning. We work with your team to understand the full picture before proposing a technical path.",
    points: ["Compliance-oriented systems analysis", "Infrastructure architecture blueprint", "Workflow and integration mapping", "Risk reduction and scalability planning"],
    cta: "Begin Discovery", ctaSymbol: "↗",
  },
  {
    id: "build", badge: "Phase 02 – 05", glyph: "◈", tag: "Core Build",
    title: "Platform Development",
    description: "From MVP to full enterprise deployment — HIPAA-conscious architecture, encrypted workflows, and scalable infrastructure built around your organization.",
    points: ["HIPAA-conscious platform architecture", "Secure data handling pipelines", "Role-based access and MFA systems", "Audit logging and compliance controls", "Cross-platform deployment"],
    cta: "Explore Development", ctaSymbol: "↗",
  },
  {
    id: "operations", badge: "Ongoing", glyph: "◉", tag: "Operations",
    title: "Platform Operations",
    description: "Sustained infrastructure support, security oversight, and continuous platform evolution. Your system stays secure, stable, and ready to scale.",
    points: ["Infrastructure monitoring", "Security patch management", "Performance optimization", "Feature enhancements and scaling", "Dedicated operational support"],
    cta: "Discuss Operations", ctaSymbol: "↗",
  },
];

function APSCard({ tier, hovered, setHovered }) {
  return (
    <div
      style={{
        background: "linear-gradient(160deg, #112418 0%, #0a1a16 100%)",
        border: `1px solid ${hovered ? "rgba(102,213,197,0.28)" : "rgba(53,143,128,0.2)"}`,
        padding: "32px 28px 32px",
        display: "flex",
        flexDirection: "column",
        transition: `all ${MOTION.medium} ${MOTION.ease}`,
        boxShadow: hovered ? GLOW.card : "none",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        position: "relative",
        overflow: "hidden",
        minHeight: 440,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage:
            "repeating-linear-gradient(-45deg, transparent 0px, transparent 14px, rgba(54,196,168,0.03) 14px, rgba(54,196,168,0.03) 15px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, transparent, ${hovered ? "rgba(54,196,168,0.5)" : "transparent"}, transparent)`,
          transition: `all ${MOTION.medium} ease`,
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 28,
          position: "relative",
          zIndex: 1,
        }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: PALETTE.teal500,
            border: "1px solid rgba(53,143,128,0.3)",
            padding: "4px 10px",
            background: "rgba(54,196,168,0.05)",
          }}>
          {tier.badge}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            letterSpacing: "0.25em",
            color: PALETTE.teal700,
            textTransform: "uppercase",
          }}>
          {tier.tag}
        </span>
      </div>
      <div
        style={{
          fontSize: "22px",
          color: hovered ? PALETTE.teal400 : PALETTE.teal600,
          marginBottom: 16,
          position: "relative",
          zIndex: 1,
          transition: `color ${MOTION.fast} ease`,
        }}>
        {tier.glyph}
      </div>
      <h3
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 400,
          fontSize: "clamp(20px, 2vw, 26px)",
          lineHeight: 1.1,
          color: hovered ? PALETTE.white : "rgba(240,250,247,0.9)",
          marginBottom: 20,
          position: "relative",
          zIndex: 1,
          transition: `color ${MOTION.fast} ease`,
        }}>
        {tier.title}
      </h3>
      <div
        style={{
          height: "1px",
          background:
            "linear-gradient(90deg, rgba(53,143,128,0.25), transparent)",
          marginBottom: 20,
        }}
      />
      <p
        style={{
          fontFamily: "var(--font-body)",
          fontWeight: 300,
          fontSize: "13px",
          lineHeight: 1.8,
          color: PALETTE.whiteMid,
          marginBottom: 24,
          flexGrow: 1,
          position: "relative",
          zIndex: 1,
        }}>
        {tier.description}
      </p>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: "0 0 28px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          position: "relative",
          zIndex: 1,
        }}>
        {tier.points.map((pt, i) => (
          <li
            key={i}
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "12px",
              lineHeight: 1.6,
              color: PALETTE.whiteMid,
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
            }}>
            <span
              style={{
                color: PALETTE.teal500,
                flexShrink: 0,
                fontSize: 10,
                marginTop: 2,
              }}>
              ◌
            </span>
            {pt}
          </li>
        ))}
      </ul>
      <a
        href="https://advanced.digitalbillionsgroup.com"
        target="_blank"
        rel="noopener noreferrer"
        className="pricing-cta-btn"
        style={{ position: "relative", zIndex: 1 }}>
        <span>{tier.cta}</span>
        <span aria-hidden="true">{tier.ctaSymbol}</span>
      </a>
    </div>
  );
}

function APSSection() {
  const [hoveredCard, setHoveredCard] = useState(null);
  return (
    <section
      id="aps"
      className="aps-section section-pad"
      style={{ position: "relative", overflow: "hidden" }}>
      {/* Stars background — second use */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          backgroundImage: `url(${starsImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          opacity: 0.06,
          mixBlendMode: "screen",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "5%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "80%",
          height: "500px",
          background: `radial-gradient(ellipse at center, rgba(54,196,168,0.05), transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <ParticleField style={{ opacity: 0.2 }} />
      <VignetteOverlay strength={0.55} color="#0f1f1a" />
      <div style={{ position: "relative", zIndex: 2 }}>
        <div
          className="gsap-reveal"
          style={{ textAlign: "center", marginBottom: 80 }}>
          <p className="teal-label" style={{ marginBottom: 20 }}>
            ◈ &nbsp; Advanced Platform Services &nbsp; ◈
          </p>
          <GlowLine style={{ margin: "0 auto 40px", maxWidth: 120 }} animated />
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(32px, 5vw, 72px)",
              lineHeight: 0.95,
              letterSpacing: "-0.02em",
              color: PALETTE.white,
              marginBottom: 24,
            }}>
            Built for Organizations
            <br />
            <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>
              That Can't Afford to Fail.
            </em>
          </h2>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "18px",
              lineHeight: 1.75,
              color: PALETTE.whiteMid,
              maxWidth: "640px",
              margin: "0 auto 20px",
            }}>
            Some platforms carry real responsibility. Patient records. Regulated
            data. Critical infrastructure. Our APS team builds for exactly that.
          </p>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "16px",
              lineHeight: 1.75,
              color: PALETTE.whiteSoft,
              maxWidth: "560px",
              margin: "0 auto",
            }}>
            Healthcare organizations, compliance-driven enterprises, and
            multi-location platforms engage our Advanced Platform Services team
            for end-to-end infrastructure design, HIPAA-conscious architecture,
            and sustained operational excellence.
          </p>
        </div>

        <div
          className="gsap-reveal stagger-container"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 1,
            background: PALETTE.border,
            border: `1px solid ${PALETTE.border}`,
            marginBottom: 80,
          }}>
          {[
            {
              icon: "◉",
              label: "HIPAA-Conscious Architecture",
              desc: "Systems designed with administrative, technical, and operational safeguards from the ground up.",
            },
            {
              icon: "◈",
              label: "Scalable Infrastructure",
              desc: "Multi-location deployments, white-label frameworks, and enterprise-grade operational ecosystems.",
            },
            {
              icon: "▫",
              label: "Sustained Operations",
              desc: "Long-term platform support, security oversight, and continuous infrastructure evolution.",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="stagger-child"
              style={{
                background: "#112418",
                padding: "40px 32px",
                borderTop: "2px solid transparent",
                transition: `all ${MOTION.medium} ${MOTION.ease}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#162820";
                e.currentTarget.style.borderTopColor = PALETTE.teal600;
                e.currentTarget.style.boxShadow =
                  "0 0 24px rgba(54,196,168,0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#112418";
                e.currentTarget.style.borderTopColor = "transparent";
                e.currentTarget.style.boxShadow = "none";
              }}>
              <div
                style={{
                  fontSize: 24,
                  color: PALETTE.teal500,
                  marginBottom: 16,
                }}>
                {f.icon}
              </div>
              <p
                className="teal-label"
                style={{ marginBottom: 12, color: PALETTE.teal400 }}>
                {f.label}
              </p>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontWeight: 300,
                  fontSize: "14px",
                  lineHeight: 1.75,
                  color: PALETTE.whiteMid,
                }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>

        {/* APS Tier Cards — no prices, redesigned with texture */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "1px",
            background: PALETTE.border,
            border: `1px solid ${PALETTE.border}`,
            marginBottom: 60,
          }}
          className="gsap-reveal">
          {apsTiers.map((tier, i) => (
            <APSCard
              key={tier.id}
              tier={tier}
              hovered={hoveredCard === i}
              setHovered={(v) => setHoveredCard(v ? i : null)}
            />
          ))}
        </div>

        <div className="cta-band gsap-reveal" style={{ marginTop: 60 }}>
          <div>
            <p className="teal-label" style={{ marginBottom: 16 }}>
              ◈ &nbsp; Speak with the APS Team
            </p>
            <p
              style={{
                fontFamily: "var(--font-body)",
                fontWeight: 300,
                fontSize: "15px",
                lineHeight: 1.75,
                color: PALETTE.whiteMid,
                maxWidth: "480px",
              }}>
              Advanced Platform Services engagements begin with a strategic
              discovery session. If your organization operates in a regulated
              environment or requires enterprise-grade infrastructure, we're
              ready to have that conversation.
            </p>
          </div>
          <div className="cta-band-buttons">
            <a
              href="https://advanced.digitalbillionsgroup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-teal">
              <span>Visit APS Portal</span>
              <span aria-hidden="true">↗</span>
            </a>
            <a
              href="mailto:hello@digitalbillionsgroup.com"
              className="btn-outline">
              <span>Contact APS Team</span>
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   CONTACT + FOOTER — unified closing section
   Two-column: editorial statement left / details right
   Footer integrated as bottom stripe
───────────────────────────────────────────── */
function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact"
      style={{
        background: `linear-gradient(160deg, #0f1f1a 0%, ${PALETTE.ink} 100%)`,
        position: "relative",
        overflow: "hidden",
        borderTop: `1px solid ${PALETTE.border}`,
      }}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: "20%",
          right: "5%",
          width: "480px",
          height: "480px",
          background: `radial-gradient(circle, rgba(54,196,168,0.06), transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "15%",
          left: "3%",
          width: "320px",
          height: "320px",
          background: `radial-gradient(circle, rgba(36,130,119,0.04), transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 32,
          left: 32,
          opacity: 0.3,
          zIndex: 2,
        }}>
        <BlueprintCorner position="tl" ink={PALETTE.teal700} size={32} />
      </div>
      <div
        style={{
          position: "absolute",
          top: 32,
          right: 32,
          opacity: 0.3,
          zIndex: 2,
        }}>
        <BlueprintCorner position="tr" ink={PALETTE.teal700} size={32} />
      </div>
      <VignetteOverlay strength={0.45} color={PALETTE.ink} />

      <div className="section-pad" style={{ position: "relative", zIndex: 2 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "80px",
            alignItems: "start",
          }}>
          {/* Left — editorial statement */}
          <div className="gsap-reveal">
            <p className="teal-label" style={{ marginBottom: 20 }}>
              ◈ &nbsp; Where It Begins
            </p>
            <GlowLine style={{ marginBottom: 36, maxWidth: 48 }} />
            <h2
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 300,
                fontSize: "clamp(36px, 5.5vw, 80px)",
                lineHeight: 0.92,
                letterSpacing: "-0.025em",
                color: PALETTE.white,
                marginBottom: 36,
              }}>
              Every platform
              <br />
              starts with a<br />
              <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>
                clear conversation.
              </em>
            </h2>
            <p
              style={{
                fontFamily: "var(--font-body)",
                fontWeight: 300,
                fontSize: "17px",
                lineHeight: 1.85,
                color: PALETTE.whiteMid,
                maxWidth: "440px",
                marginBottom: 40,
              }}>
              We work closely with every client before writing a single line of
              code. Whether you're mapping a new AI workflow, architecting a
              platform, or exploring what's possible — the first step is a real
              conversation with our team.
            </p>
            <p
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: "18px",
                lineHeight: 1.6,
                color: PALETTE.teal400,
                borderLeft: `2px solid ${PALETTE.teal700}`,
                paddingLeft: "20px",
                textShadow: GLOW.text,
              }}>
              Every engagement starts with understanding, not assumptions.
            </p>
            <div
              style={{
                display: "flex",
                gap: 16,
                marginTop: 44,
                flexWrap: "wrap",
              }}>
              <a
                href="mailto:hello@digitalbillionsgroup.com"
                className="btn-teal">
                <span>Start a Conversation</span>
                <span aria-hidden="true">↗</span>
              </a>
              <a
                href="https://advanced.digitalbillionsgroup.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline">
                <span>APS Enterprise</span>
                <span aria-hidden="true">◈</span>
              </a>
            </div>
          </div>

          {/* Right — contact details */}
          <div className="gsap-reveal" data-reveal-delay="150">
            <svg
              width="100%"
              height="140"
              viewBox="0 0 480 140"
              style={{ marginBottom: 48, opacity: 0.35 }}>
              <line
                x1="0"
                y1="70"
                x2="480"
                y2="70"
                stroke={PALETTE.teal700}
                strokeWidth="0.5"
              />
              {[60, 120, 180, 240, 300, 360, 420].map((x, i) => (
                <g key={i}>
                  <line
                    x1={x}
                    y1="60"
                    x2={x}
                    y2="80"
                    stroke={PALETTE.teal700}
                    strokeWidth="0.5"
                  />
                  <circle
                    cx={x}
                    cy="70"
                    r={i === 3 ? 4 : 2}
                    fill={i === 3 ? PALETTE.teal500 : "none"}
                    stroke={PALETTE.teal700}
                    strokeWidth="0.5"
                  />
                </g>
              ))}
              <text
                x="240"
                y="24"
                textAnchor="middle"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "9px",
                  letterSpacing: "0.3em",
                  fill: PALETTE.teal700,
                }}>
                COMM NODE ◌ ACTIVE
              </text>
              <text
                x="240"
                y="120"
                textAnchor="middle"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "9px",
                  letterSpacing: "0.3em",
                  fill: PALETTE.teal800,
                }}>
                DBG ◌ GLOBAL ◌ MMXXVI
              </text>
            </svg>

            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <div
                style={{
                  paddingBottom: 36,
                  borderBottom: `1px solid ${PALETTE.border}`,
                  marginBottom: 36,
                }}>
                <p className="teal-label" style={{ marginBottom: 14 }}>
                  ✉ &nbsp; All Inquiries
                </p>
                <a
                  href="mailto:hello@digitalbillionsgroup.com"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 300,
                    fontSize: "clamp(16px, 2vw, 24px)",
                    color: PALETTE.white,
                    textDecoration: "none",
                    transition: `all ${MOTION.fast} ease`,
                    display: "block",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = PALETTE.teal400;
                    e.currentTarget.style.textShadow = GLOW.text;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = PALETTE.white;
                    e.currentTarget.style.textShadow = "none";
                  }}>
                  hello@digitalbillionsgroup.com
                </a>
              </div>

              <div
                style={{
                  paddingBottom: 36,
                  borderBottom: `1px solid ${PALETTE.border}`,
                  marginBottom: 36,
                }}>
                <p className="teal-label" style={{ marginBottom: 14 }}>
                  ◈ &nbsp; APS Enterprise Portal
                </p>
                <a
                  href="https://advanced.digitalbillionsgroup.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "13px",
                    letterSpacing: "0.12em",
                    color: PALETTE.teal500,
                    textDecoration: "none",
                    transition: `color ${MOTION.fast} ease`,
                    display: "block",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = PALETTE.teal300;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = PALETTE.teal500;
                  }}>
                  advanced.digitalbillionsgroup.com ↗
                </a>
              </div>

              <div
                style={{
                  paddingBottom: 36,
                  borderBottom: `1px solid ${PALETTE.border}`,
                  marginBottom: 36,
                }}>
                <p className="teal-label" style={{ marginBottom: 14 }}>
                  ◌ &nbsp; Our Commitment
                </p>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontWeight: 300,
                    fontSize: "14px",
                    lineHeight: 1.8,
                    color: PALETTE.whiteMid,
                  }}>
                  Every inquiry receives a personal response from our team — no
                  automated replies. We respond because we care about
                  understanding your project before proposing a path forward.
                </p>
              </div>

              <div>
                <p
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "9px",
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    color: PALETTE.whiteFaint,
                    lineHeight: 2.4,
                  }}>
                  Digital Billions Group
                  <br />
                  Worldwide · Online · Always Available.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Integrated footer stripe */}
      <div
        data-testid="footer"
        style={{
          borderTop: `1px solid ${PALETTE.border}`,
          padding: "24px var(--pad-x)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          zIndex: 2,
          background:
            "linear-gradient(180deg, transparent, rgba(10,18,16,0.6))",
        }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: PALETTE.teal800,
          }}>
          ◈ DIGITAL BILLIONS GROUP &nbsp; ◌ &nbsp; MMXXII—MMXXVI
        </span>
        <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "9px",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: PALETTE.teal800,
            }}>
            REFINE · AUTOMATE · CREATE
          </span>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <a
              href="mailto:hello@digitalbillionsgroup.com"
              className="nav-link"
              style={{ color: PALETTE.teal800 }}>
              Contact
            </a>
            <a
              href="https://aps.digitalbillionsgroup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link"
              style={{ color: PALETTE.teal800 }}>
              APS ↗
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() { return null; }

/* ─────────────────────────────────────────────
   APP
───────────────────────────────────────────── */
function App() {
  const [bootDone, setBootDone] = useState(false);
  useLenis();
  useGSAPReveal('.gsap-reveal');
  useStaggerReveal('.stagger-container');
  return (
    <>
      {!bootDone && <BootSequence onComplete={() => setBootDone(true)} />}
      <NoiseTexture opacity={0.03} />
      <ScrollProgress />
      <Nav />
      <main data-testid="main-wrapper">
        <Hero />
        <About />
        <Services />
        <AtmosphericDivider />
        <AIServices />
        <WebDevServices />
        <APSSection />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

export default App;
